import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, CheckCircle } from "lucide-react";

export default function PatientInfoForm({
  formData,
  idValidation,
  idTouched,
  onInputChange,
  onIdBlur,
  readOnly = false,
}) {
  const inputClassName = readOnly ? "bg-gray-50 cursor-not-allowed" : "";
  // Clear condition variables
  const hasIdValidation = !!idValidation;
  const isIdValid = hasIdValidation && idValidation.isValid;
  const isIdInvalid = hasIdValidation && !idValidation.isValid;
  const shouldShowIdErrors = idTouched && isIdInvalid;
  const shouldShowIdDetails = isIdValid && idValidation.details;

  const getIdInputClassName = () => {
    if (shouldShowIdErrors) return "border-red-500 pr-10";
    if (isIdValid) return "border-green-500 pr-10";
    return "pr-10";
  };

  const renderIdValidationIcon = () => {
    if (!hasIdValidation) return null;

    return (
      <div className="absolute right-2 top-1/2 -translate-y-1/2">
        {isIdValid ? (
          <CheckCircle className="w-5 h-5 text-green-500" />
        ) : (
          <AlertCircle className="w-5 h-5 text-red-500" />
        )}
      </div>
    );
  };

  const renderIdErrors = () => {
    if (!shouldShowIdErrors) return null;

    return (
      <div className="text-xs text-red-500">
        {idValidation.errors.join(", ")}
      </div>
    );
  };

  const renderIdDetails = () => {
    if (!shouldShowIdDetails) return null;

    const { gender, dateOfBirth } = idValidation.details;
    const genderText = gender === "male" ? "Male" : "Female";
    const birthDate = `${dateOfBirth.day}/${dateOfBirth.month}/${dateOfBirth.year}`;

    return (
      <div className="text-xs text-green-600">
        Valid ID • {genderText} • Born: {birthDate}
      </div>
    );
  };

  const renderPatientIdField = () => (
    <div className="space-y-2">
      <Label htmlFor="patient_id">Patient ID Number *</Label>
      <div className="relative">
        <Input
          id="patient_id"
          value={formData.patient_id}
          onChange={(e) => onInputChange("patient_id", e.target.value)}
          onBlur={onIdBlur}
          placeholder="Enter SA ID number (13 digits)"
          required
          maxLength={13}
          className={`${getIdInputClassName()} ${inputClassName}`}
          readOnly={readOnly}
        />
        {renderIdValidationIcon()}
      </div>
      {renderIdErrors()}
      {renderIdDetails()}
    </div>
  );

  const renderEmailField = () => (
    <div className="space-y-2">
      <Label htmlFor="patient_email">Patient Email *</Label>
      <Input
        id="patient_email"
        type="email"
        value={formData.patient_email}
        onChange={(e) => onInputChange("patient_email", e.target.value)}
        placeholder="patient@example.com"
        required
        className={inputClassName}
        readOnly={readOnly}
      />
      <p className="text-xs text-gray-500">Email for appointment notifications</p>
    </div>
  );

  const renderNotesField = () => (
    <div className="space-y-2">
      <Label htmlFor="notes">Notes</Label>
      <Textarea
        id="notes"
        value={formData.notes}
        onChange={(e) => onInputChange("notes", e.target.value)}
        placeholder="Any special instructions or notes..."
        rows={3}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      {renderPatientIdField()}
      {renderEmailField()}
      {renderNotesField()}
    </div>
  );
}